import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import {
  OrbitControls,
  Grid,
  TransformControls,
  Environment,
  Stats,
  Box,
  Sphere,
  Cylinder,
  Plane,
  Html,
  Text,
  ContactShadows,
  AdaptiveDpr,
  PerformanceMonitor,
} from '@react-three/drei';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useSceneStore } from '@/stores/scene-store';
import { Api } from '@/services/generated';
import {
  Move3D,
  RotateCcw,
  Scale,
  Grid3X3,
  Monitor,
  Save,
  Cpu,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Settings,
  Layers,
  Box as BoxIcon,
  Square,
} from 'lucide-react';
import { toast } from 'sonner';
import * as THREE from 'three';
import { cn } from '@/lib/utils';

interface Scene3DEditorProps {
  sceneId: string;
}

type TransformMode = 'translate' | 'rotate' | 'scale';
type ObjectType = 'wall' | 'floor' | 'ceiling' | 'pillar' | 'equipment' | 'rack' | 'table' | 'room' | 'device' | 'sensor';

// Enhanced 3D Object Component
function SceneObject3D({
  object,
  isSelected,
  onClick,
  devices,
}: {
  object: any;
  isSelected: boolean;
  onClick: () => void;
  devices: any[];
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { size, color, opacity, material, geometry, text } = object;

  const getMaterial = () => {
    const baseProps = {
      color: color ?? '#ffffff',
      transparent: opacity < 1,
      opacity: opacity ?? 1,
    };

    switch (material) {
      case 'glass':
        return <meshPhysicalMaterial {...baseProps} transmission={0.9} roughness={0.1} thickness={0.5} />;
      case 'metal':
        return <meshStandardMaterial {...baseProps} metalness={0.8} roughness={0.2} />;
      case 'concrete':
        return <meshStandardMaterial {...baseProps} roughness={0.8} />;
      default:
        return <meshStandardMaterial {...baseProps} />;
    }
  };

  const width = size?.width ?? 1;
  const height = size?.height ?? 1;
  const depth = size?.depth ?? 1;

  const handleClick = (e: any) => {
    e.stopPropagation();
    onClick();
  };

  const handlePointerOver = (e: any) => {
    e.stopPropagation();
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = () => {
    document.body.style.cursor = 'auto';
  };

  const renderGeometry = () => {
    switch (geometry) {
      case 'cylinder':
        return (
          <Cylinder
            ref={meshRef}
            args={[width / 2, width / 2, height, 32]}
            onClick={handleClick}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
          >
            {getMaterial()}
          </Cylinder>
        );
      case 'sphere':
        return (
          <Sphere
            ref={meshRef}
            args={[width / 2, 32, 32]}
            onClick={handleClick}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
          >
            {getMaterial()}
          </Sphere>
        );
      case 'plane':
        return (
          <Plane
            ref={meshRef}
            args={[width, height]}
            onClick={handleClick}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
          >
            {getMaterial()}
          </Plane>
        );
      default:
        return (
          <Box
            ref={meshRef}
            args={[width, height, depth]}
            onClick={handleClick}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
          >
            {getMaterial()}
          </Box>
        );
    }
  };

  return (
    <group
      position={[object.position.x, object.position.y, object.position.z]}
      rotation={[object.rotation.x, object.rotation.y, object.rotation.z]}
      scale={[object.scale.x, object.scale.y, object.scale.z]}
    >
      {renderGeometry()}

      {/* Object label */}
      {text && (
        <Text
          position={[0, height / 2 + 0.5, 0]}
          fontSize={0.3}
          color={color ?? '#000000'}
          anchorX="center"
          anchorY="middle"
        >
          {text}
        </Text>
      )}

      {/* Selection outline */}
      {isSelected && (
        <group scale={[1.02, 1.02, 1.02]}>
          {geometry === 'cylinder' ? (
            <Cylinder args={[width / 2, width / 2, height, 32]}>
              <meshBasicMaterial color="#3b82f6" wireframe />
            </Cylinder>
          ) : geometry === 'sphere' ? (
            <Sphere args={[width / 2, 32, 32]}>
              <meshBasicMaterial color="#3b82f6" wireframe />
            </Sphere>
          ) : geometry === 'plane' ? (
            <Plane args={[width, height]}>
              <meshBasicMaterial color="#3b82f6" wireframe />
            </Plane>
          ) : (
            <Box args={[width, height, depth]}>
              <meshBasicMaterial color="#3b82f6" wireframe />
            </Box>
          )}
        </group>
      )}

      {/* Attached devices */}
      {object.attachedDevices?.map((deviceId: string, index: number) => {
        const device = devices.find(d => d.id === deviceId);
        if (!device) return null;

        return (
          <DeviceAttachment
            key={deviceId}
            device={device}
            position={[
              ((index % 3) - 1) * 0.7,
              height / 2 + 0.3,
              ((Math.floor(index / 3) % 3) - 1) * 0.7,
            ]}
          />
        );
      })}
    </group>
  );
}

// Device Attachment Component
function DeviceAttachment({
  device,
  position,
}: {
  device: any;
  position: [number, number, number];
}) {
  return (
    <group position={position}>
      {/* Device representation */}
      <Box args={[0.3, 0.15, 0.3]}>
        <meshStandardMaterial color="#4a5568" />
      </Box>

      {/* Device label */}
      <Html position={[0, 0.3, 0]} center>
        <div className="bg-black/80 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
          📱 {device.name ?? device.serial_number}
        </div>
      </Html>

      {/* Sensors */}
      {device.sensors?.map((sensor: any, index: number) => (
        <SensorVisualization
          key={sensor.id}
          sensor={sensor}
          position={[
            ((index % 2) - 0.5) * 0.2,
            0.2,
            ((Math.floor(index / 2) % 2) - 0.5) * 0.2,
          ]}
        />
      ))}
    </group>
  );
}

// Sensor Visualization Component
function SensorVisualization({
  sensor,
  position,
}: {
  sensor: any;
  position: [number, number, number];
}) {
  const getSensorColor = () => {
    switch (sensor.type) {
      case 'temperature': return '#ef4444';
      case 'humidity': return '#3b82f6';
      case 'light': return '#eab308';
      case 'motion': return '#8b5cf6';
      case 'pressure': return '#06d6a0';
      case 'vibration': return '#f72585';
      default: return '#64748b';
    }
  };

  const getSensorIcon = () => {
    switch (sensor.type) {
      case 'temperature': return '🌡️';
      case 'humidity': return '💧';
      case 'light': return '💡';
      case 'motion': return '📷';
      case 'pressure': return '⚡';
      case 'vibration': return '📳';
      default: return '📡';
    }
  };

  return (
    <group position={position}>
      <Sphere args={[0.05]}>
        <meshStandardMaterial color={getSensorColor()} emissive={getSensorColor()} emissiveIntensity={0.3} />
      </Sphere>
      
      <Html position={[0, 0.15, 0]} center>
        <div className="bg-black/80 text-white px-1 py-0.5 rounded text-xs whitespace-nowrap">
          {getSensorIcon()} {sensor.name}
          {sensor.latest_data && (
            <div className="text-xs opacity-75">
              {sensor.latest_data.value} {sensor.latest_data.unit}
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

// Transform Controls Wrapper
function TransformControlsWrapper({
  children,
  mode,
  enabled,
  object,
  onObjectChange,
}: {
  children: React.ReactNode;
  mode: TransformMode;
  enabled: boolean;
  object: any;
  onObjectChange?: (object: any) => void;
}) {
  const transformRef = useRef<any>();
  const { camera, gl } = useThree();

  useEffect(() => {
    if (transformRef.current && enabled && object) {
      transformRef.current.attach(object);
    } else if (transformRef.current) {
      transformRef.current.detach();
    }
  }, [enabled, object]);

  const handleChange = useCallback(() => {
    if (object && onObjectChange) {
      onObjectChange(object);
    }
  }, [object, onObjectChange]);

  return (
    <>
      {children}
      <TransformControls
        ref={transformRef}
        camera={camera}
        domElement={gl.domElement}
        mode={mode}
        onChange={handleChange}
        showX={mode !== 'scale'}
        showY={mode !== 'scale'}
        showZ={mode !== 'scale'}
      />
    </>
  );
}

// Enhanced Toolbar Component
function Toolbar({
  transformMode,
  setTransformMode,
  showGrid,
  setShowGrid,
  showStats,
  setShowStats,
  onSave,
  objectType,
  setObjectType,
  onAddObject,
}: {
  transformMode: TransformMode;
  setTransformMode: (mode: TransformMode) => void;
  showGrid: boolean;
  setShowGrid: (show: boolean) => void;
  showStats: boolean;
  setShowStats: (show: boolean) => void;
  onSave: () => void;
  objectType: ObjectType;
  setObjectType: (type: ObjectType) => void;
  onAddObject: () => void;
}) {
  return (
    <Card className="absolute top-4 left-4 z-10">
      <CardContent className="p-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Object Type Selection */}
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={objectType === 'device' ? 'default' : 'outline'}
              onClick={() => setObjectType('device')}
              title="Device"
            >
              <Cpu className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={objectType === 'wall' ? 'default' : 'outline'}
              onClick={() => setObjectType('wall')}
              title="Wall"
            >
              <Square className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={objectType === 'floor' ? 'default' : 'outline'}
              onClick={() => setObjectType('floor')}
              title="Floor"
            >
              <BoxIcon className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={objectType === 'equipment' ? 'default' : 'outline'}
              onClick={() => setObjectType('equipment')}
              title="Equipment"
            >
              <Monitor className="w-4 h-4" />
            </Button>
          </div>
          
          <Separator orientation="vertical" className="h-6" />
          
          {/* Transform Mode */}
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={transformMode === 'translate' ? 'default' : 'outline'}
              onClick={() => setTransformMode('translate')}
              title="Move"
            >
              <Move3D className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={transformMode === 'rotate' ? 'default' : 'outline'}
              onClick={() => setTransformMode('rotate')}
              title="Rotate"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={transformMode === 'scale' ? 'default' : 'outline'}
              onClick={() => setTransformMode('scale')}
              title="Scale"
            >
              <Scale className="w-4 h-4" />
            </Button>
          </div>
          
          <Separator orientation="vertical" className="h-6" />
          
          {/* Actions */}
          <Button size="sm" onClick={onAddObject} title="Add Object">
            <Plus className="w-4 h-4" />
          </Button>
          
          <Separator orientation="vertical" className="h-6" />
          
          {/* View Options */}
          <Button
            size="sm"
            variant={showGrid ? 'default' : 'outline'}
            onClick={() => setShowGrid(!showGrid)}
            title="Toggle Grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          
          <Button
            size="sm"
            variant={showStats ? 'default' : 'outline'}
            onClick={() => setShowStats(!showStats)}
            title="Toggle Stats"
          >
            <Monitor className="w-4 h-4" />
          </Button>
          
          <Separator orientation="vertical" className="h-6" />
          
          {/* Save */}
          <Button size="sm" onClick={onSave} title="Save Scene">
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Object Explorer Component
function ObjectExplorer({
  objects,
  selectedObjectId,
  onSelectObject,
  onDeleteObject,
  onToggleVisibility,
  onDuplicateObject,
}: {
  objects: any[];
  selectedObjectId: string | null;
  onSelectObject: (id: string) => void;
  onDeleteObject: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onDuplicateObject: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Objects ({objects.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {objects.map((obj) => (
          <div
            key={obj.id}
            className={cn(
              "p-2 rounded cursor-pointer transition-colors group",
              selectedObjectId === obj.id
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            )}
            onClick={() => onSelectObject(obj.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: obj.color || '#64748b' }} />
                <span className="text-sm truncate capitalize">
                  {obj.text || obj.type}
                </span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-6 h-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisibility(obj.id);
                  }}
                >
                  {obj.visible !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-6 h-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicateObject(obj.id);
                  }}
                >
                  <Copy className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-6 h-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteObject(obj.id);
                  }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {objects.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No objects in scene. Click to add objects.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Properties Panel Component
function PropertiesPanel({
  selectedObject,
  devices,
  onUpdateObject,
  onAttachDevice,
  onDetachDevice,
}: {
  selectedObject: any;
  devices: any[];
  onUpdateObject: (updates: any) => void;
  onAttachDevice: (deviceId: string) => void;
  onDetachDevice: (deviceId: string) => void;
}) {
  if (!selectedObject) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Properties
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Select an object to edit properties</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Properties
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Transform */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Transform</Label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">X</Label>
              <Input
                type="number"
                value={selectedObject.position?.x || 0}
                onChange={(e) => onUpdateObject({
                  position: { ...selectedObject.position, x: Number(e.target.value) }
                })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Y</Label>
              <Input
                type="number"
                value={selectedObject.position?.y || 0}
                onChange={(e) => onUpdateObject({
                  position: { ...selectedObject.position, y: Number(e.target.value) }
                })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Z</Label>
              <Input
                type="number"
                value={selectedObject.position?.z || 0}
                onChange={(e) => onUpdateObject({
                  position: { ...selectedObject.position, z: Number(e.target.value) }
                })}
                className="h-8"
              />
            </div>
          </div>
        </div>

        {/* Size */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Size</Label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">W</Label>
              <Input
                type="number"
                value={selectedObject.size?.width || 1}
                onChange={(e) => onUpdateObject({
                  size: { ...selectedObject.size, width: Number(e.target.value) }
                })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">H</Label>
              <Input
                type="number"
                value={selectedObject.size?.height || 1}
                onChange={(e) => onUpdateObject({
                  size: { ...selectedObject.size, height: Number(e.target.value) }
                })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">D</Label>
              <Input
                type="number"
                value={selectedObject.size?.depth || 1}
                onChange={(e) => onUpdateObject({
                  size: { ...selectedObject.size, depth: Number(e.target.value) }
                })}
                className="h-8"
              />
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Appearance</Label>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Color</Label>
              <Input
                type="color"
                value={selectedObject.color || '#ffffff'}
                onChange={(e) => onUpdateObject({ color: e.target.value })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Opacity</Label>
              <Slider
                value={[selectedObject.opacity || 1]}
                onValueChange={([value]) => onUpdateObject({ opacity: value })}
                max={1}
                min={0}
                step={0.1}
                className="py-2"
              />
            </div>
            <div>
              <Label className="text-xs">Material</Label>
              <Select
                value={selectedObject.material || 'standard'}
                onValueChange={(value) => onUpdateObject({ material: value })}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="glass">Glass</SelectItem>
                  <SelectItem value="metal">Metal</SelectItem>
                  <SelectItem value="concrete">Concrete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Attached Devices */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Attached Devices</Label>
          <div className="space-y-1">
            {selectedObject.attachedDevices?.map((deviceId: string) => {
              const device = devices.find(d => d.id === deviceId);
              return (
                <div key={deviceId} className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-xs">{device?.name || device?.serial_number}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-6 h-6 p-0"
                    onClick={() => onDetachDevice(deviceId)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
            <Select onValueChange={(deviceId) => onAttachDevice(deviceId)}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Attach device..." />
              </SelectTrigger>
              <SelectContent>
                {devices
                  .filter(d => !selectedObject.attachedDevices?.includes(d.id))
                  .map((device) => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.name || device.serial_number}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Scene3DEditor Component
export function Scene3DEditor({ sceneId }: Scene3DEditorProps) {
  const {
    scenes,
    currentScene,
    setCurrentScene,
    addObject,
    updateObject,
    deleteObject,
    selectedObject,
    setSelectedObject,
  } = useSceneStore();

  // Fetch devices for the scene
  const { data: devicesData } = Api.useGetDevicesQuery({
    includeSensors: 'true',
  });

  const devices = useMemo(() => {
    if (!devicesData?.data || !currentScene?.deviceIds) return [];
    return devicesData.data.filter(device => 
      currentScene.deviceIds.includes(device.id)
    );
  }, [devicesData?.data, currentScene?.deviceIds]);

  const [transformMode, setTransformMode] = useState<TransformMode>('translate');
  const [selectedMeshObject, setSelectedMeshObject] = useState<any>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [objectType, setObjectType] = useState<ObjectType>('wall');

  useEffect(() => {
    // Load scene from store
    const scene = scenes.find(s => s.id === sceneId);
    if (scene && scene.id !== currentScene?.id) {
      setCurrentScene(scene);
    }
  }, [sceneId, scenes, currentScene?.id, setCurrentScene]);

  const handleObjectClick = useCallback((objectId: string, meshObject: any) => {
    setSelectedObject(objectId);
    setSelectedMeshObject(meshObject);
  }, [setSelectedObject]);

  const handleObjectChange = useCallback((meshObject: any) => {
    if (selectedObject && currentScene) {
      updateObject(currentScene.id, selectedObject, {
        position: {
          x: meshObject.position.x,
          y: meshObject.position.y,
          z: meshObject.position.z,
        },
        rotation: {
          x: meshObject.rotation.x,
          y: meshObject.rotation.y,
          z: meshObject.rotation.z,
        },
        scale: {
          x: meshObject.scale.x,
          y: meshObject.scale.y,
          z: meshObject.scale.z,
        },
      });
    }
  }, [selectedObject, currentScene, updateObject]);

  const handleSave = useCallback(async () => {
    if (currentScene) {
      try {
        toast.success('Scene saved successfully!');
      } catch (error) {
        toast.error('Failed to save scene');
        console.error('Save failed:', error);
      }
    }
  }, [currentScene]);

  const handleAddObject = useCallback(() => {
    if (currentScene) {
      const newObject = {
        type: objectType,
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        size: { width: 2, height: 1, depth: 2 },
        color: objectType === 'wall' ? '#cccccc' : objectType === 'floor' ? '#8B4513' : '#ffffff',
        opacity: 1,
        material: (objectType === 'wall' ? 'concrete' : 'standard') as 'basic' | 'standard' | 'glass' | 'metal' | 'concrete',
        geometry: (objectType === 'floor' ? 'plane' : 'box') as 'box' | 'cylinder' | 'sphere' | 'plane',
        visible: true,
        attachedDevices: [],
      };
      
      addObject(currentScene.id, newObject);
    }
  }, [currentScene, objectType, addObject]);

  const handleCanvasClick = useCallback((event: any) => {
    // Clear selection when clicking empty space
    if (event.object?.type === 'Mesh' && !event.object.userData?.objectId) {
      setSelectedObject(null);
      setSelectedMeshObject(null);
    }
  }, [setSelectedObject]);

  const selectedObjectData = currentScene?.objects?.find(obj => obj.id === selectedObject);

  if (!currentScene) {
    return (
      <div className="flex items-center justify-center h-full">
        <div>Loading scene...</div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* 3D Viewport */}
      <div className="flex-1 relative">
        <Canvas
          camera={{ position: [15, 15, 15], fov: 50 }}
          shadows
          onClick={handleCanvasClick}
          gl={{ antialias: true, alpha: false }}
          dpr={[1, 2]}
        >
          {/* Lighting */}
          <ambientLight intensity={0.4} />
          <directionalLight
            position={[20, 20, 10]}
            intensity={1}
            castShadow
            shadow-mapSize={[2048, 2048]}
            shadow-camera-near={0.1}
            shadow-camera-far={50}
            shadow-camera-left={-20}
            shadow-camera-right={20}
            shadow-camera-top={20}
            shadow-camera-bottom={-20}
          />
          <pointLight position={[-10, -10, -10]} intensity={0.3} />

          {/* Environment */}
          <Environment preset="city" />
          <ContactShadows
            position={[0, -0.01, 0]}
            opacity={0.3}
            scale={100}
            blur={2.5}
            far={50}
          />

          {/* Grid */}
          {showGrid && (
            <Grid
              args={[50, 50]}
              position={[0, 0, 0]}
              cellSize={1}
              cellThickness={0.5}
              cellColor="#64748b"
              sectionSize={10}
              sectionThickness={1}
              sectionColor="#374151"
              fadeDistance={100}
              infiniteGrid
            />
          )}

          {/* Scene Objects */}
          <TransformControlsWrapper
            mode={transformMode}
            enabled={!!selectedMeshObject}
            object={selectedMeshObject}
            onObjectChange={handleObjectChange}
          >
            {currentScene.objects
              ?.filter(obj => obj.visible !== false)
              ?.map((object) => (
                <SceneObject3D
                  key={object.id}
                  object={object}
                  isSelected={selectedObject === object.id}
                  onClick={() => handleObjectClick(object.id, null)}
                  devices={devices}
                />
              ))}
          </TransformControlsWrapper>

          {/* Controls */}
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            dampingFactor={0.05}
            screenSpacePanning={false}
            minDistance={5}
            maxDistance={100}
            maxPolarAngle={Math.PI / 2}
          />

          {/* Performance */}
          <AdaptiveDpr pixelated />
          <PerformanceMonitor />

          {/* Stats */}
          {showStats && <Stats />}
        </Canvas>

        {/* Toolbar */}
        <Toolbar
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          showStats={showStats}
          setShowStats={setShowStats}
          onSave={handleSave}
          objectType={objectType}
          setObjectType={setObjectType}
          onAddObject={handleAddObject}
        />

        {/* Instructions */}
        <Card className="absolute bottom-4 left-4 z-10">
          <CardContent className="p-3">
            <div className="text-sm text-muted-foreground">
              <div className="font-medium mb-1">Controls:</div>
              <div>• Select object type and click Add to create</div>
              <div>• Click objects to select and transform</div>
              <div>• Mouse: Orbit • Scroll: Zoom • Right-click: Pan</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Sidebar */}
      <div className="w-80 border-l bg-card flex flex-col">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Scene Editor</h2>
          <p className="text-sm text-muted-foreground">{currentScene.name}</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Object Explorer */}
          <ObjectExplorer
            objects={currentScene.objects || []}
            selectedObjectId={selectedObject}
            onSelectObject={(id) => {
              setSelectedObject(id);
              setSelectedMeshObject(null);
            }}
            onDeleteObject={(id) => {
              if (currentScene) {
                deleteObject(currentScene.id, id);
                if (selectedObject === id) {
                  setSelectedObject(null);
                  setSelectedMeshObject(null);
                }
              }
            }}
            onToggleVisibility={(id) => {
              if (currentScene) {
                const obj = currentScene.objects?.find(o => o.id === id);
                if (obj) {
                  updateObject(currentScene.id, id, { visible: obj.visible !== false ? false : true });
                }
              }
            }}
            onDuplicateObject={(id) => {
              if (currentScene) {
                const obj = currentScene.objects?.find(o => o.id === id);
                if (obj) {
                  addObject(currentScene.id, {
                    ...obj,
                    position: { ...obj.position, x: obj.position.x + 2 },
                  });
                }
              }
            }}
          />

          {/* Properties Panel */}
          <PropertiesPanel
            selectedObject={selectedObjectData}
            devices={devices}
            onUpdateObject={(updates) => {
              if (selectedObject && currentScene) {
                updateObject(currentScene.id, selectedObject, updates);
              }
            }}
            onAttachDevice={(deviceId) => {
              if (selectedObject && currentScene && selectedObjectData) {
                const attachedDevices = selectedObjectData.attachedDevices || [];
                updateObject(currentScene.id, selectedObject, {
                  attachedDevices: [...attachedDevices, deviceId],
                });
              }
            }}
            onDetachDevice={(deviceId) => {
              if (selectedObject && currentScene && selectedObjectData) {
                const attachedDevices = selectedObjectData.attachedDevices || [];
                updateObject(currentScene.id, selectedObject, {
                  attachedDevices: attachedDevices.filter((id: string) => id !== deviceId),
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
